// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
// import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors"; // Store Custom Code
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Img, { ImgProps } from "@insite/mobius/Img";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "css";
import * as React from "react";
import { FC } from "react";
import { css } from "styled-components";

/* Starter Custom Code - Start */
import getCssUnitField from "@store/Framework/Common/Fields/Toolkit/cssUnit";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
import cssProp from "@toolkit/Framework/Common/Utilities/cssProp";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { spacingStyles } from "@toolkit/Themes/common/mixins";
/* Starter Custom Code - End */

/* Store Custom Code - Start */
import { useGetLink } from "@store/Framework/Store/Links/LinksSelectors";
/* Store Custom Code - End */

const enum fields {
    imageUrl = "imageUrl",
    altText = "altText",
    imageLink = "imageLink",
    customCSS = "customCSS",
    // Starter Custom Code
    imageWidth = "imageWidth",
    imageAlignment = "imageAlignment",
}

/* Starter Custom Code - Start */
const imageWidthField = getCssUnitField(fields.imageWidth);
/* Starter Custom Code - End */

interface OwnProps extends WidgetProps {
    fields: {
        [fields.imageUrl]: string;
        [fields.altText]: string;
        [fields.imageLink]: LinkFieldValue;
        [fields.customCSS]: string;
        // Starter Custom Code
        [fields.imageWidth]: string;
        [fields.imageAlignment]: string;
    };
}

export interface ImageStyles {
    wrapper?: InjectableCss;
    /**
     * @deprecated Use img instead
     */
    image?: LazyImageProps;
    img?: ImgProps;
    imageLink?: LinkPresentationProps;
    // Starter Custom Code
    imageWrapper?: InjectableCss;
}

export const imageStyles: ImageStyles = {
    wrapper: {
        css: spacingStyles(),
    },
    imageWrapper: {
        css: css`
            display: inline-flex;
            vertical-align: top;
            max-width: 100%;

            * {
                width: 100% !important; /* Make sure sub elements scale with the configurable width */
            }
        `,
    },
    img: {
        css: css`
            background-color: transparent;
            display: block;
            width: auto;
            max-width: 100%;
            height: auto;
        `,
    },
};

export const Image: FC<OwnProps> = ({ fields }) => {
    const { url } = useGetLink(fields.imageLink);
    const baseStyles = useMergeStyles("image", imageStyles); // Starter Custom Code

    /* Starter Custom Code - Start */
    const styles = React.useMemo(() => {
        return mergeProps<ImageStyles>(imageStyles, {
            imageWrapper: {
                css: css`
                    ${breakpointValues(imageWidthField.parse(fields.imageWidth), "width")}
                `,
            },
        });
    }, [baseStyles, fields.imageWidth]);
    /* Starter Custom Code - End */

    const image = <Img className="img" src={fields.imageUrl} altText={fields.altText} loading="lazy" {...styles.img} />;

    const customCssWrapper = {
        css: css`
            ${styles.wrapper?.css /* Starter Custom Code */}
            ${cssProp("text-align", fields.imageAlignment) /* Starter Custom Code */}
            ${fields.customCSS}
        `,
    };

    return (
        <StyledWrapper {...customCssWrapper}>
            <StyledWrapper className="img-wrapper" {...styles.imageWrapper /* Starter Custom Code */}>
                {url ? (
                    <Link className="img-link" href={url} {...styles.imageLink}>
                        {image}
                    </Link>
                ) : (
                    image
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const defaultCustomCss = `.img-wrapper {
}

.img-link {
}

.img {
}
`;

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: Image,
    definition: {
        group: "Basic",
        icon: "image",
        fieldDefinitions: [
            {
                name: fields.imageUrl,
                fieldType: "Contextual",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                tab: basicTab,
            },
            {
                name: fields.altText,
                fieldType: "Contextual",
                editorTemplate: "TextField",
                defaultValue: "",
                tab: basicTab,
            },
            {
                name: fields.imageLink,
                fieldType: "Contextual",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                tab: basicTab,
            },
            /* Starter Custom Code - Start */
            {
                ...imageWidthField.definition,
                fieldType: "Contextual",
                tab: basicTab,
            },
            {
                name: fields.imageAlignment,
                displayName: "Image Alignment",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Left", value: "left" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Right", value: "right" },
                ],
                defaultValue: "left",
                hideEmptyOption: true,
                fieldType: "Contextual",
                tab: basicTab,
            },
            /* Starter Custom Code - End */
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "Contextual",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
